/**
 * @fileoverview The one-line box for a photo's words, with the quick-note pills under it.
 *
 * Shared by uploading a photo and editing one, so the two never grow
 * slightly different boxes; the only difference is the wording, which says
 * whether the words already exist. It holds no state: the dialog's
 * usePhotoNote owns the words and the rules for saving them.
 *
 * Where: the photo screens (UploadPhotoDialog and EditPhotoDialog), under
 * the date.
 * API: none; the dialog's usePhotoNote saves the words. The server's
 * limits.NOTE_MAX_LENGTH (lib/growSettings) caps the box.
 * Settings: none.
 * Words (en/es): plants.notes.* (photoLabel, photoLabelExisting,
 * photoPlaceholderNew, photoPlaceholderExisting, photoHint,
 * photoHintExisting); QuickNoteChips' own pills.
 *
 * @module PhotoNoteField
 */

import React from 'react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { QuickNoteChips } from '@/shared/components/QuickNoteChips';

/** The words, where changes go, and which of the two sentences to use. */
interface PhotoNoteFieldProps {
  /** The words so far. */
  value: string;
  /** Called with the new words, already cut to the longest note allowed. */
  onChange: (next: string) => void;
  /** While the dialog is busy. */
  disabled?: boolean;
  /**
   * Whether these words already exist.
   *
   * Editing and writing are the same act on the same field, but they are not
   * the same sentence. "Add a note, saved to this plant's notes, dated today"
   * is three untrue things when you are changing a caption that was written
   * last year and is deliberately kept out of the journal.
   */
  existing?: boolean;
}

/**
 * The words that go with a photo.
 *
 * One field for both halves of the dialog — writing a photo's note when it is
 * uploaded, and changing it afterwards — because they are the same act and
 * should not drift into two slightly different boxes.
 *
 * A pill fills the box rather than saving. Tapping is composing here: the
 * wording can be added to, and nothing is written until the photo is.
 *
 * @param props - PhotoNoteFieldProps.
 */
export const PhotoNoteField: React.FC<PhotoNoteFieldProps> = ({
  value, onChange, disabled = false, existing = false,
}) => {
  const { reactTranslate: t } = useTranslation();
  const limits = useGrowLimits();

  /**
   * Adds a pill's words after whatever is typed, within the length limit.
   *
   * @param label - The pill's words.
   */
  const append = (label: string): void => {
    const next = value.trim() ? `${value.trim()} ${label}` : label;
    onChange(next.slice(0, limits.NOTE_MAX_LENGTH));
  };

  /*
   * One line, with the quick notes as one row under it. It was a label, a
   * two-line box, the pills wrapping onto as many rows as they needed and a
   * hint underneath: four or five lines on a screen that has to fit a
   * photograph. The label and the hint are now the placeholder, and the hint
   * is still read out to a screen reader.
   */
  const hintId = React.useId();
  return (
    <div className="shrink-0 space-y-1.5">
      <input
        id="photo-note"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, limits.NOTE_MAX_LENGTH))}
        maxLength={limits.NOTE_MAX_LENGTH}
        disabled={disabled}
        placeholder={existing ? t('plants.notes.photoPlaceholderExisting') : t('plants.notes.photoPlaceholderNew')}
        aria-label={existing ? t('plants.notes.photoLabelExisting') : t('plants.notes.photoLabel')}
        aria-describedby={hintId}
        className="h-11 w-full rounded-md border border-input bg-card px-3 text-[0.9375rem] outline-none focus:border-primary disabled:opacity-60"
      />
      <span id={hintId} className="sr-only">
        {existing ? t('plants.notes.photoHintExisting') : t('plants.notes.photoHint')}
      </span>
      <QuickNoteChips
        onPick={append}
        disabled={disabled}
        className="flex-nowrap overflow-x-auto pb-0.5 [&>*]:shrink-0 [scrollbar-width:none]"
      />
    </div>
  );
};
